/* Cosa e' stato fatturato a un cliente: la sua storia in un posto solo.
 *
 * Dal 16 ottobre 2026. Prima la scheda del cliente diceva solo quanto deve ancora e come ha pagato: per
 * sapere cosa gli era stato fatturato bisognava cercare in tre elenchi (Avvisi, Fatture Smart, Archivio).
 * Qui c'e' tutto, dal piu' recente, per anno:
 *
 * - in cima quattro numeri (cosa resta da fatturare, cosa resta da incassare, quanto e' stato mandato e
 *   quanto incassato quest'anno), ognuno un collegamento a dove si agisce;
 * - sotto, i documenti: l'avviso con quello che contiene, a che punto e', e la fattura che ne e' nata;
 *   cliccando si apre il documento intero;
 * - in fondo, la storia dei versamenti (anche quelli annullati, barrati).
 *
 * Non calcola niente di fiscale: gli importi sono quelli del server, qui si sommano solo per i riepiloghi, in
 * modo esatto (`somma`). Lo vede chi lavora sui documenti, non chi imputa le ore.
 */

import { Link } from "react-router-dom";
import { documenti, incassi } from "../api";
import { useCarica } from "../carica";
import {
  conta,
  dataBreve,
  dataCompleta,
  eZero,
  euro,
  nomeDocumento,
  nomeStatoDocumento,
  somma,
  type Cliente,
  type Documento,
  type Maturato,
  type SituazioneCliente,
} from "../dominio";

/** Dove si apre un documento: la scheda in cui di solito lo si lavora. */
function dove(d: Documento): string {
  const id = encodeURIComponent(d.identificativo);
  if (d.tipo === "AVVISO") return `/fatturare/avvisi?documento=${id}`;
  if (d.tipo === "FATTURA" && d.stato === "BOZZA") return `/fatturare/fatture-smart?documento=${id}`;
  return `/fatturare/archivio?documento=${id}`;
}

function anno(iso: string): number {
  return Number(iso.slice(0, 4));
}

export function FatturatoCliente({ cliente }: { cliente: Cliente }) {
  const id = cliente.identificativo;
  const docs = useCarica<Documento[]>(() => documenti.delCliente(id), [id]);
  const aspetta = useCarica<Maturato[]>(() => documenti.daFatturare(id), [id]);
  const situazione = useCarica<SituazioneCliente>(() => incassi.situazioneCliente(id), [id]);

  const errore = docs.errore ?? aspetta.errore ?? situazione.errore;
  const pronto = docs.dati !== undefined && aspetta.dati !== undefined && situazione.dati !== undefined;

  return (
    <section className="scheda-sezione fatturato-cliente">
      <div className="sezione-testa">
        <h3>Cosa gli abbiamo fatturato</h3>
      </div>
      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      {!pronto ? (
        errore ? null : <p className="attesa">Un momento.</p>
      ) : (
        <Contenuto cliente={cliente} docs={docs.dati!} aspetta={aspetta.dati!} situazione={situazione.dati!} />
      )}
    </section>
  );
}

function Contenuto({
  cliente,
  docs,
  aspetta,
  situazione,
}: {
  cliente: Cliente;
  docs: Documento[];
  aspetta: Maturato[];
  situazione: SituazioneCliente;
}) {
  const adesso = new Date().getFullYear();
  const daFatturare = aspetta.filter((m) => !eZero(m.da_fatturare));
  const valoreDaFatturare = somma(daFatturare.map((m) => m.da_fatturare));
  const avvisi = docs.filter((d) => d.tipo === "AVVISO");
  const mandatiQuestAnno = avvisi.filter(
    (d) => (d.stato === "EMESSO" || d.stato === "INCASSATO") && anno(d.data) === adesso,
  );
  const incassatoQuestAnno = situazione.movimenti.filter((m) => !m.annullato && anno(m.data) === adesso);
  const avvisiPerId = new Map(avvisi.map((d) => [d.identificativo, d]));

  if (docs.length === 0 && daFatturare.length === 0) {
    return <p className="aiuto">Per ora niente: nessun documento e nessun lavoro da fatturare.</p>;
  }

  // Per anno, dal piu' recente.
  const anni = [...new Set(docs.map((d) => anno(d.data)))].sort((a, b) => b - a);

  return (
    <>
      <div className="numeri-testa numeri-piccoli">
        <Link
          className="numero-testa"
          to={`/fatturare/da-fatturare?cerca=${encodeURIComponent(cliente.denominazione)}`}
        >
          <span className="numero-testa-cifra">{euro(valoreDaFatturare)}</span>
          <span className="numero-testa-nome">
            da fatturare{daFatturare.length > 0 && `, ${conta(daFatturare.length, "pratica", "pratiche")}`}
          </span>
        </Link>
        <div className={`numero-testa${eZero(situazione.da_incassare) ? "" : " attenzione"}`}>
          <span className="numero-testa-cifra">{euro(situazione.da_incassare)}</span>
          <span className="numero-testa-nome">da incassare</span>
        </div>
        <div className="numero-testa">
          <span className="numero-testa-cifra">{euro(somma(mandatiQuestAnno.map((d) => d.netto_a_pagare)))}</span>
          <span className="numero-testa-nome">avvisi mandati nel {adesso}</span>
        </div>
        <div className="numero-testa">
          <span className="numero-testa-cifra">{euro(somma(incassatoQuestAnno.map((m) => m.importo)))}</span>
          <span className="numero-testa-nome">incassato nel {adesso}</span>
        </div>
      </div>

      {docs.length === 0 ? (
        <p className="aiuto">Nessun documento ancora: il lavoro fatto aspetta di essere fatturato.</p>
      ) : (
        anni.map((a, indice) => {
          const deiQuesto = docs.filter((d) => anno(d.data) === a);
          const avvisiQuesto = deiQuesto.filter((d) => d.tipo === "AVVISO" && d.stato !== "ANNULLATO");
          const residuo = somma(avvisiQuesto.filter((d) => d.stato === "EMESSO").map((d) => d.residuo ?? d.netto_a_pagare));
          return (
            <details key={a} className="anno-cliente" open={indice === 0}>
              <summary>
                <strong>{a}</strong>{" "}
                <span className="narrativa">
                  {conta(deiQuesto.length, "documento", "documenti")}
                  {!eZero(residuo) && `, ancora da incassare ${euro(residuo)}`}
                </span>
              </summary>
              <table className="tabella documenti-cliente">
                <tbody>
                  {deiQuesto.map((d) => (
                    <RigaDocumento key={d.identificativo} d={d} avvisoDiOrigine={d.origine ? avvisiPerId.get(d.origine) : undefined} />
                  ))}
                </tbody>
              </table>
            </details>
          );
        })
      )}

      {situazione.movimenti.length > 0 && (
        <details className="archivio">
          <summary>Storia dei pagamenti, {conta(situazione.movimenti.length, "versamento", "versamenti")}</summary>
          <table className="tabella">
            <tbody>
              {situazione.movimenti.map((m) => (
                <tr key={m.identificativo} className={m.annullato ? "versamento-annullato" : undefined}>
                  <td>
                    <span className="pratica-nome">
                      {dataCompleta(m.data)}, {m.modalita_nome}
                    </span>
                    <span className="narrativa">
                      {m.descrizione_avviso}
                      {m.annullato && ` · annullato: ${m.annullato_motivo}`}
                      {!m.annullato && m.fattura_riferimento && ` · fattura n. ${m.fattura_riferimento}`}
                    </span>
                  </td>
                  <td className="cifra colonna-importo">{euro(m.importo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

/** Il tono del punto in cui e' un documento: chiuso bene (verde), che aspetta qualcosa (ambra), spento. */
function tono(d: Documento): "ok" | "attesa" | "spento" {
  if (d.stato === "ANNULLATO" || d.stato === "STORNATO" || d.stato === "BOZZA" && d.tipo === "AVVISO") return "spento";
  if (d.tipo === "AVVISO") return d.stato === "INCASSATO" ? "ok" : "attesa";
  if (d.tipo === "FATTURA") return d.stato === "BOZZA" ? "attesa" : "ok";
  return "ok";
}

function RigaDocumento({ d, avvisoDiOrigine }: { d: Documento; avvisoDiOrigine: Documento | undefined }) {
  const titolo = d.tipo === "AVVISO" ? "Avviso di parcella" : `${nomeDocumento(d.tipo)}${d.numero ? ` n. ${d.numero}` : ""}`;
  const voci = d.righe.map((r) => r.descrizione).filter(Boolean);
  const contiene = voci.length > 0 ? voci.join(", ") : d.descrizione;
  const compenso = d.righe.length > 0 ? somma(d.righe.map((r) => r.importo)) : null;
  const annullato = d.stato === "ANNULLATO" || d.stato === "STORNATO";
  const ultimoIncasso = d.incassi
    .filter((i) => !i.annullato)
    .map((i) => i.data)
    .sort()
    .at(-1);

  let punto = nomeStatoDocumento(d.tipo, d.stato);
  if (d.tipo === "AVVISO" && d.stato === "EMESSO") {
    const manca = d.residuo ?? d.netto_a_pagare;
    punto = `da incassare ${euro(manca)}${!eZero(d.incassato) ? ` (già ${euro(d.incassato)})` : ""}`;
  } else if (d.tipo === "AVVISO" && d.stato === "INCASSATO" && ultimoIncasso) {
    punto = `incassato il ${dataBreve(ultimoIncasso)}`;
  }

  return (
    <tr className={annullato ? "documento-annullato" : undefined}>
      <td className="doc-principale">
        <span className="doc-testa">
          <Link className="pratica-nome" to={dove(d)}>
            {titolo}
          </Link>
          <span className="doc-data">{dataBreve(d.data)}</span>
        </span>
        <span className="narrativa riga-contiene">
          {contiene}
          {avvisoDiOrigine && ` (dall'avviso del ${dataBreve(avvisoDiOrigine.data)})`}
        </span>
        {compenso !== null && d.tipo === "AVVISO" && (
          <span className="narrativa">compenso {euro(compenso)}, totale {euro(d.totale_documento)}</span>
        )}
        <span className={`chip-stato chip-${tono(d)}`}>{punto}</span>
      </td>
      <td className="cifra colonna-importo">{euro(d.tipo === "AVVISO" ? d.netto_a_pagare : d.totale_documento)}</td>
    </tr>
  );
}
